import type { Polygon, Position } from "geojson";

export type GeoJsonImportResult =
  | { ok: true; polygons: Polygon[]; polygonCount: number; vertexCount: number }
  | { ok: false; error: string };

const LNG_MIN = -180;
const LNG_MAX = 180;
const LAT_MIN = -90;
const LAT_MAX = 90;

const isFinitePair = (value: unknown): value is Position =>
  Array.isArray(value) && value.length >= 2 && Number.isFinite(value[0]) && Number.isFinite(value[1]);

const isInRange = (position: Position): boolean =>
  position[0] >= LNG_MIN && position[0] <= LNG_MAX && position[1] >= LAT_MIN && position[1] <= LAT_MAX;

const closeRing = (ring: Position[]): Position[] => {
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return ring;
  return [...ring, first];
};

const toPolygon = (coordinates: unknown): Polygon => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    throw new Error("Polygone invalide : coordonnées manquantes.");
  }

  const rings = coordinates.map((ring, ringIndex) => {
    if (!Array.isArray(ring) || !ring.every(isFinitePair)) {
      throw new Error("Polygone invalide : les coordonnées doivent être des paires de nombres.");
    }
    if (!ring.every(isInRange)) {
      throw new Error("Coordonnées hors limites. Format attendu : [longitude, latitude].");
    }
    const closed = closeRing(ring);
    const distinctCount = new Set(closed.slice(0, -1).map((position) => `${position[0]},${position[1]}`)).size;
    if (distinctCount < 3) {
      throw new Error(
        ringIndex === 0
          ? "Un polygone doit avoir au moins 3 sommets distincts."
          : "Un trou de polygone doit avoir au moins 3 sommets distincts.",
      );
    }
    return closed;
  });

  return { type: "Polygon", coordinates: rings };
};

// Non-polygon geometries are skipped so a mixed FeatureCollection still yields its polygons.
const extractPolygons = (node: unknown): Polygon[] => {
  if (!node || typeof node !== "object") return [];
  const geo = node as {
    type?: string;
    coordinates?: unknown;
    geometry?: unknown;
    features?: unknown;
    geometries?: unknown;
  };

  switch (geo.type) {
    case "Polygon":
      return [toPolygon(geo.coordinates)];
    case "MultiPolygon":
      if (!Array.isArray(geo.coordinates)) throw new Error("MultiPolygone invalide : coordonnées manquantes.");
      return geo.coordinates.map(toPolygon);
    case "Feature":
      return extractPolygons(geo.geometry);
    case "FeatureCollection":
      return Array.isArray(geo.features) ? geo.features.flatMap(extractPolygons) : [];
    case "GeometryCollection":
      return Array.isArray(geo.geometries) ? geo.geometries.flatMap(extractPolygons) : [];
    default:
      return [];
  }
};

const countVertices = (polygons: Polygon[]): number =>
  polygons.reduce(
    (total, polygon) => total + polygon.coordinates.reduce((sum, ring) => sum + Math.max(0, ring.length - 1), 0),
    0,
  );

export const parsePastedGeoJson = (input: string): GeoJsonImportResult => {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, error: "Collez un GeoJSON à importer." };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: "JSON invalide." };
  }

  try {
    const polygons = extractPolygons(parsed);
    if (polygons.length === 0) {
      return { ok: false, error: "Aucun polygone trouvé. Attendu un Polygon ou un MultiPolygon." };
    }
    return { ok: true, polygons, polygonCount: polygons.length, vertexCount: countVertices(polygons) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "GeoJSON invalide." };
  }
};
